import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSeo } from "@/utils/seo";
import { ArrowRight, ArrowUpRight, Check, FileDown, Layers, Leaf, Phone, Mail, PlugZap, Zap } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useLang } from "@/i18n";

/* ============ JULTEC JRM-Serie (Multischalter) ============ */
const MS_COLS_DE = ["1 Satellit", "2 Satelliten", "4 Satelliten"];
const MS_COLS_EN = ["1 satellite", "2 satellites", "4 satellites"];
const MS_ROWS = [
    { outsDe: "8 Ausgänge", outsEn: "8 outputs", models: ["JRM0508M / T", "JRM0908M / T", "JRM1708M / T"] },
    { outsDe: "12 Ausgänge", outsEn: "12 outputs", models: ["JRM0512M / T", "JRM0912M / T", "JRM1712M / T"] },
    { outsDe: "16 Ausgänge", outsEn: "16 outputs", models: ["JRM0516M / T", "JRM0916M / T", "JRM1716M / T"] },
];
const MS_HINTS = [
    {
        de: "Multischalter kommen zum Einsatz, wenn an einer Ableitung genau ein Sat-Empfangsteil betrieben wird.",
        en: "Multi-switches are used when exactly one satellite receive part is operated on a drop.",
    },
    {
        de: "Alle JRM-Geräte sind extrem energiesparend — und laufen auch in der Kaskade ohne Netzteil.",
        en: "All JRM units are extremely energy-efficient — and even run in cascades without a power supply.",
    },
    {
        de: "Kaskadieren mit Schnellverbindern Typ JFQ1: -M startet die Kaskade, -T schließt sie ab (alternativ -M mit DC-entkoppelten 75-Ω-Abschlusswiderständen).",
        en: "Cascade with the JFQ1 quick connectors: -M starts the cascade, -T terminates it (alternatively -M with DC-decoupled 75 Ω terminating resistors).",
    },
    {
        de: "Große Kaskadensysteme: kombinierbar mit Kaskadenstartverstärkern und Mehrfachabzweigern.",
        en: "Large cascade systems: can be combined with cascade start amplifiers and multi-tap units.",
    },
    {
        de: "Twin-Aufrüstung einzelner Ableitungen über das JRS-Einkabelprogramm — Multischalter und Einkabelumsetzer lassen sich auch gemischt kaskadieren.",
        en: "Twin upgrades for individual drops via the JRS single-cable programme — multi-switches and single-cable transmodulators can also be cascaded in a mix.",
    },
];

/* ============ JULTEC JRS-System ============ */
const JRS_FEATURES = [
    {
        icon: PlugZap,
        de: {
            t: "Kein Netzteil, kein Allgemeinstrom",
            d: "Die Empfänger speisen die Umsetzer — und sogar die LNBs. Ideal für Effizienzhäuser und alle Orte, wo am Verteiler keine Steckdose zur Verfügung steht.",
        },
        en: {
            t: "No power supply, no mains power",
            d: "The receivers power the transmodulators — and even the LNBs. Ideal for low-energy buildings and anywhere there is no socket at the distribution point.",
        },
    },
    {
        icon: Zap,
        de: {
            t: "Ein Receiver versorgt alles",
            d: "JRS-Systeme sind so ausgelegt, dass ein einzelner Empfänger das gesamte Netz speisen kann — mit großzügiger Reserve gegenüber der von ASTRA geforderten Dauerstromabgabe von 350 mA.",
        },
        en: {
            t: "One receiver powers everything",
            d: "JRS systems are designed so that a single receiver can power the entire network — with generous headroom versus the 350 mA continuous current required by ASTRA.",
        },
    },
    {
        icon: Layers,
        de: {
            t: "Legacy & CSS/JESS automatisch",
            d: "Jeder Ausgang erkennt selbstständig Legacy-Empfänger (DiSEqC) oder Channel Stacking nach EN 50494 bzw. JESS (EN 50607) — bis zu 16 Userbänder je Ableitung.",
        },
        en: {
            t: "Legacy & CSS/JESS automatically",
            d: "Every output automatically detects legacy receivers (DiSEqC) or channel stacking to EN 50494 / JESS (EN 50607) — up to 16 user bands per drop.",
        },
    },
    {
        icon: Leaf,
        de: {
            t: "α²CSS — analoge Doppelkonvertierung",
            d: "Statt stromhungriger Digitaltechnik arbeitet a²CSS analog: minimaler Verbrauch, hohe Dynamik, weniger Abwärme — und damit längere Lebensdauer der Geräte.",
        },
        en: {
            t: "α²CSS — analogue double conversion",
            d: "Instead of power-hungry digital circuitry, a²CSS works analogue: minimal consumption, high dynamic range, less waste heat — and therefore a longer equipment life.",
        },
    },
];
const JRS_ROWS = [
    { tapsDe: "1 Ableitung", tapsEn: "1 drop", ub4: "—", ub8: "JRS0501-8" },
    { tapsDe: "2 Ableitungen", tapsEn: "2 drops", ub4: "JRS0502-4", ub8: "JRS0502-8" },
    { tapsDe: "4 Ableitungen", tapsEn: "4 drops", ub4: "JRS0504-4", ub8: "JRS0504-8" },
    { tapsDe: "6 Ableitungen", tapsEn: "6 drops", ub4: "JRS0506-4", ub8: "JRS0506-8" },
    { tapsDe: "8 Ableitungen", tapsEn: "8 drops", ub4: "JRS0508-4", ub8: "JRS0508-8" },
];
const JRS_COMBI = ["JRS0502-4+4T", "JRS0502-8+4T"];
const JRS_HINTS = [
    {
        de: "Quatro-LNBs mit geringer Stromaufnahme einsetzen — ideal unter 150 mA, problemlos bis 250 mA.",
        en: "Use Quatro LNBs with low current draw — ideal below 150 mA, fine up to 250 mA.",
    },
    {
        de: "Vollkupfer-Koaxialkabel verwenden; StaKu-/CCS-Kabel verkürzen die mögliche Leitungslänge deutlich.",
        en: "Use solid-copper coaxial cable; StaKu/CCS cables significantly shorten the possible cable length.",
    },
    {
        de: "In Kaskaden die Enden mit DC-entkoppelten Abschlusswiderständen terminieren.",
        en: "In cascades, terminate the ends with DC-decoupled terminating resistors.",
    },
    {
        de: "Mehrere Wohnungen je Ableitung: programmierbare Antennendosen der JAP-Serie einsetzen.",
        en: "Several homes per drop: use the programmable wall outlets of the JAP series.",
    },
    {
        de: "Kombinierbar mit netzgespeisten Geräten (JPS/JRM) — deren Netzteil übernimmt dann die LNB-Versorgung.",
        en: "Can be combined with mains-powered units (JPS/JRM) — their power supply then takes over the LNB feed.",
    },
    {
        de: "Bedarfsweise das effiziente Schaltnetzteil JNT19-2000 für die LNB-Speisung anschließen.",
        en: "If needed, connect the efficient JNT19-2000 switching power supply for the LNB feed.",
    },
];
const JRS_LINKS = [
    { href: "/datenblaetter/jultec_jrs_manual.pdf", de: "JRS-Handbuch (PDF)", en: "JRS manual (PDF)" },
    { href: "/datenblaetter/jultec_katalog_13.pdf", de: "JULTEC-Katalog Edition 13 (PDF)", en: "JULTEC catalogue edition 13 (PDF)" },
    { href: "https://jultec.de/gloss_CSS.html", de: "Technische Infos: CSS-Grundlagen (jultec.de)", en: "Technical info: CSS basics (jultec.de)" },
];

/* ============ JULTEC Glasfaser (Optik) ============ */
const OPTICAL = [
    {
        name: "JOL — Glas-Breitband-Wandler",
        nameEn: "JOL — fibre broadband converter",
        desc: "Zur Speisung von Einkabelmultischaltern und koaxialen Kaskaden (z. B. JOL0102-90A, JOL0105-88FR).",
        descEn: "For feeding single-cable multi-switches and coaxial cascades (e.g. JOL0102-90A, JOL0105-88FR).",
        img: "/produkte/jultec-jol0102-90a.webp",
    },
    {
        name: "JOS — Glas-nach-Einkabel-Wandler",
        nameEn: "JOS — fibre-to-single-cable converter",
        desc: "Für FTTH-Anwendungen — Glasfaser direkt bis zum Teilnehmer (z. B. JOS0201-16T).",
        descEn: "For FTTH applications — fibre optic right to the subscriber (e.g. JOS0201-16T).",
        img: "/produkte/jultec-jos0201-16t.webp",
    },
    {
        name: "JAO — Glasfasersender",
        nameEn: "JAO — fibre optic transmitter",
        desc: "Sendet das Satellitensignal optisch ein — zum Speisen von JOS- und JOL-Geräten (z. B. JAO0501C1550).",
        descEn: "Transmits the satellite signal optically — for feeding JOS and JOL units (e.g. JAO0501C1550).",
        img: "/produkte/jultec-jao0501c1550.webp",
    },
    {
        name: "JOM — Glas-nach-Legacy-Wandler",
        nameEn: "JOM — fibre-to-legacy converter",
        desc: "Für FTTH-Anwendungen mit klassischen Receivern (z. B. JOM0202T-FR).",
        descEn: "For FTTH applications with classic receivers (e.g. JOM0202T-FR).",
        img: null,
    },
];

/* ============ KATHREIN ============ */
const LINE_GROUPS = [
    {
        groupDe: "Multischalter (EXR)",
        groupEn: "Multi-switches (EXR)",
        rows: [
            { models: "EXR 156 · EXR 158 · EXR 1512 · EXR 1516", specDe: "1 SAT-Position | 6, 8, 12 oder 16 Teilnehmer", specEn: "1 satellite position | 6, 8, 12 or 16 subscribers" },
            { models: "EXR 2554 · EXR 2508 · EXR 2558 · EXR 58/ECO", specDe: "1 SAT-Position | 4, 8 oder 16 Teilnehmer", specEn: "1 satellite position | 4, 8 or 16 subscribers" },
            { models: "EXR 25016 · EXR 25516", specDe: "1 SAT-Position | 16 Teilnehmer", specEn: "1 satellite position | 16 subscribers" },
            { models: "EXR 2908 · EXR 2998", specDe: "2 SAT-Positionen | 8 Teilnehmer", specEn: "2 satellite positions | 8 subscribers" },
            { models: "EXR 1708 · EXR 1718", specDe: "4 SAT-Positionen | 8 Teilnehmer", specEn: "4 satellite positions | 8 subscribers" },
        ],
    },
    {
        groupDe: "Einkabel-Multischalter (EXE / EXD)",
        groupEn: "Single-cable multi-switches (EXE / EXD)",
        rows: [
            { models: "EXE 1581 · EXE 2581", specDe: "1 Ausgang | bis zu 8 Userbänder", specEn: "1 output | up to 8 user bands" },
            { models: "EXE 1512 · EXE 2512", specDe: "1 Ausgang | bis zu 12 Userbänder", specEn: "1 output | up to 12 user bands" },
            { models: "EXD 158 Twin · EXD 258 Twin", specDe: "2 Ausgänge | bis zu 16 Userbänder", specEn: "2 outputs | up to 16 user bands" },
            { models: "EXD 1524 · EXD 2524", specDe: "2 Ausgänge | bis zu 24 Userbänder", specEn: "2 outputs | up to 24 user bands" },
            { models: "EXD 1532 · EXD 2532", specDe: "2 Ausgänge | bis zu 32 Userbänder", specEn: "2 outputs | up to 32 user bands" },
            { models: "EXD 154", specDe: "4 Ausgänge | je bis zu 16 Userbänder", specEn: "4 outputs | up to 16 user bands each" },
        ],
    },
    {
        groupDe: "Kaskaden, Matrizen & Netzverstärker",
        groupEn: "Cascades, matrices & network amplifiers",
        rows: [
            { models: "EAX 2512 · EAX 2912", specDe: "2-fach-Abzweiger für 5er-/9er-Kaskaden", specEn: "2-way tap for 5/9 cascades" },
            { models: "EBX 2520 · EBX 2920", specDe: "2-fach-Verteiler für 5er-/9er-Kaskaden", specEn: "2-way splitter for 5/9 cascades" },
            { models: "EXR 121 · EXR 124 · EXR 221", specDe: "DiSEqC-Umschaltmatrizen / Twin-Zuspeisung", specEn: "DiSEqC switching matrices / twin feed-in" },
            { models: "UWS 24", specDe: "Wideband-auf-Quattro-Umsetzer | 1 SAT-Position", specEn: "Wideband-to-Quattro transmodulator | 1 satellite position" },
            { models: "VWS 2500 · VWS 2551 · VWS 2900 · VWS 2991", specDe: "Verteilnetzverstärker | 4 bzw. 8 × SAT-ZF + terrestrisch", specEn: "Distribution network amplifiers | 4 or 8 × SAT-IF + terrestrial" },
        ],
    },
];

const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

function SchemaGraphic() {
    const { t } = useLang();
    const CLUSTERS = [70, 210, 350, 490]; // Mittelpunkte der 4 Ableitungen
    const tv = (cx, clusterIdx) => (
        <g key={`${clusterIdx}-${cx}`}>
            <rect x={cx - 11} y="204" width="22" height="22" rx="3" fill="#ffffff" stroke="#334155" strokeWidth="1.8" />
            <text x={cx} y="220" textAnchor="middle" fontSize="11" fontWeight="600" fill="#334155" fontFamily={MONO}>
                TV
            </text>
        </g>
    );
    return (
        <svg
            viewBox="0 0 560 276"
            className="block w-full h-auto"
            role="img"
            aria-label={t(
                "Prinzipschema: Ein JRS-Gerät hat 4 Ausgänge, an jedem Ausgang hängen bis zu 4 Receiver — alles wird von den Receivern gespeist, kein Netzteil nötig",
                "Schematic: a JRS unit has 4 outputs, each output feeds up to 4 receivers — everything is powered by the receivers, no power supply needed"
            )}
            data-testid="jrs-schema-svg"
        >
            <text x="552" y="24" textAnchor="end" fontSize="13.5" fontWeight="600" fill="#334155" fontFamily={MONO}>
                {t("receivergespeist", "receiver-powered")}
            </text>
            <text x="552" y="43" textAnchor="end" fontSize="13.5" fill="#475569" fontFamily={MONO}>
                {t("kein Netzteil nötig", "no power supply needed")}
            </text>
            <rect x="185" y="16" width="190" height="44" rx="8" fill="#ffffff" stroke="#334155" strokeWidth="2.5" />
            <path d="M201 46 a11 11 0 0 1 16 -14" fill="none" stroke="#334155" strokeWidth="2.5" strokeLinecap="round" />
            <circle cx="215" cy="44" r="2.5" fill="#334155" />
            <text x="300" y="44" textAnchor="middle" fontSize="16" fontWeight="600" fill="#1e293b" fontFamily={MONO}>
                Quatro-LNB
            </text>
            {[255, 275, 295, 315].map((x) => (
                <line key={x} x1={x} y1="60" x2={x} y2="108" stroke="#334155" strokeWidth="2.5" />
            ))}
            <text x="330" y="92" fontSize="13.5" fill="#475569" fontFamily={MONO}>
                {t("4 Polaritäten", "4 polarities")}
            </text>
            <rect x="185" y="108" width="190" height="56" rx="8" fill="#ffffff" stroke="#334155" strokeWidth="2.5" />
            <text x="280" y="136" textAnchor="middle" fontSize="22" fontWeight="700" fill="#dc2626" fontFamily={MONO}>
                JRS
            </text>
            <text x="280" y="156" textAnchor="middle" fontSize="12" fill="#475569" fontFamily={MONO}>
                {t("4 Ausgänge × 4 Userbänder", "4 outputs × 4 user bands")}
            </text>
            {[235, 265, 295, 325].map((x, i) => (
                <line key={x} x1={x} y1="164" x2={CLUSTERS[i]} y2="200" stroke="#334155" strokeWidth="2" />
            ))}
            {[235, 265, 295, 325].map((x, i) => (
                <text key={`n${x}`} x={x + 7} y="182" fontSize="12" fill="#64748b" fontFamily={MONO}>
                    {i + 1}
                </text>
            ))}
            {CLUSTERS.map((cx, i) => (
                <g key={cx}>
                    <line x1={cx - 36} y1="200" x2={cx + 36} y2="200" stroke="#334155" strokeWidth="2" />
                    {[cx - 36, cx - 12, cx + 12, cx + 36].map((tx) => (
                        <line key={tx} x1={tx} y1="200" x2={tx} y2="204" stroke="#334155" strokeWidth="2" />
                    ))}
                    {[cx - 36, cx - 12, cx + 12, cx + 36].map((tx) => tv(tx, i))}
                </g>
            ))}
            <text x="280" y="264" textAnchor="middle" fontSize="14.5" fontWeight="600" fill="#334155" fontFamily={MONO}>
                {t("4 Ausgänge × 4 Receiver = bis zu 16 Teilnehmer", "4 outputs × 4 receivers = up to 16 subscribers")}
            </text>
        </svg>
    );
}

const KATHREIN_CARDS = [
    {
        name: "EXR 1516",
        desc: "1 SAT-Position | 16 Teilnehmer, kaskadierbar.",
        descEn: "1 satellite position | 16 subscribers, cascadable.",
        img: "/produkte/kathrein-exr-1516.webp",
    },
    {
        name: "EXR 2508",
        desc: "1 SAT-Position | 8 Teilnehmer, kaskadierbar.",
        descEn: "1 satellite position | 8 subscribers, cascadable.",
        img: "/produkte/kathrein-exr-2508.webp",
    },
    {
        name: "EXR 2908",
        desc: "2 SAT-Positionen | 8 Teilnehmer.",
        descEn: "2 satellite positions | 8 subscribers.",
        img: "/produkte/kathrein-exr-2908.webp",
    },
    {
        name: "EXR 1708",
        desc: "4 SAT-Positionen | 8 Teilnehmer.",
        descEn: "4 satellite positions | 8 subscribers.",
        img: "/produkte/kathrein-exr-1708.webp",
    },
    {
        name: "EXE 2512",
        desc: "Einkabel: 1 Ausgang | bis zu 12 Userbänder.",
        descEn: "Single-cable: 1 output | up to 12 user bands.",
        img: "/produkte/kathrein-exe-2512.webp",
    },
    {
        name: "EXD 2532",
        desc: "Einkabel Twin: 2 Ausgänge | bis zu 32 Userbänder.",
        descEn: "Single-cable twin: 2 outputs | up to 32 user bands.",
        img: "/produkte/kathrein-exd-2532.webp",
    },
    {
        name: "VWS 2991",
        desc: "Verteilnetzverstärker | 8 × SAT-ZF + terrestrisch.",
        descEn: "Distribution network amplifier | 8 × SAT-IF + terrestrial.",
        img: "/produkte/kathrein-vws-2991.webp",
    },
];

function Cta() {
    const { t } = useLang();
    return (
        <Reveal>
            <div className="mt-16 rounded-2xl bg-slate-950 text-white p-8 sm:p-12 relative overflow-hidden" data-testid="satzf-cta">
                <div className="absolute inset-0 bg-blueprint-dark opacity-60" aria-hidden="true" />
                <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                    <div>
                        <p className="eyebrow text-red-300">{t("Beratung & Bestellung", "Advice & ordering")}</p>
                        <h2 className="mt-3 font-display text-xl sm:text-2xl font-bold">
                            {t("SAT-ZF-Verteiltechnik? Wir dimensionieren und liefern.", "SAT-IF distribution technology? We size it and supply it.")}
                        </h2>
                        <p className="mt-2 text-slate-400 text-sm max-w-xl">
                            {t(
                                "Jultec und Kathrein ab Lager oder beschaffbar — mit Auslegungshilfe und Lieferung deutschlandweit & ins EU-Ausland.",
                                "Jultec and Kathrein ex stock or procurable — with design assistance and delivery throughout Germany & the EU."
                            )}
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-3 shrink-0">
                        <a
                            href="tel:+49919594040"
                            data-testid="satzf-phone-cta"
                            className="inline-flex items-center gap-2 rounded-full bg-white text-slate-900 hover:bg-red-50 font-semibold text-sm px-6 py-3 transition-colors"
                        >
                            <Phone className="h-4 w-4" />
                            +49 (0) 9195 / 9404-0
                        </a>
                        <a
                            href="mailto:vertrieb@satmueller.de?subject=Anfrage%3A%20SAT-ZF-Verteilsysteme"
                            data-testid="satzf-mail-cta"
                            className="inline-flex items-center gap-2 rounded-full border border-white/25 hover:border-white/60 font-semibold text-sm px-6 py-3 transition-colors"
                        >
                            <Mail className="h-4 w-4" />
                            {t("Anfrage senden", "Send request")}
                        </a>
                    </div>
                </div>
            </div>
        </Reveal>
    );
}

export default function SatZfPage() {
    const { t } = useLang();
    const location = useLocation();
    useSeo(
        t("SAT-ZF-Verteilsysteme: Jultec JRM, JRS, Glasfaser & Kathrein | Müller", "SAT-IF distribution: Jultec JRM, JRS, fibre optic & Kathrein | Müller"),
        t(
            "SAT-ZF-Verteiltechnik für Mehrteilnehmeranlagen: JRM-Multischalter, das receivergespeiste JRS-System, Glasfaser-Optik und Kathrein Line — bei Müller Hemhofen.",
            "SAT-IF distribution technology for multi-subscriber systems: JRM multi-switches, the receiver-powered JRS system, fibre optics and Kathrein Line — at Müller Hemhofen."
        ),
    );

    useEffect(() => {
        if (location.hash) {
            const el = document.getElementById(location.hash.slice(1));
            if (el) {
                const timer = setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
                return () => clearTimeout(timer);
            }
        }
        window.scrollTo(0, 0);
    }, [location]);

    const chip = "inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:border-red-300 hover:text-red-700 transition-colors";

    return (
        <main data-testid="page-sat-zf" className="pt-28 sm:pt-32 pb-20 sm:pb-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                <p className="font-mono text-[11px] uppercase tracking-widest text-slate-400" data-testid="breadcrumb">
                    <Link to="/produkte" data-testid="breadcrumb-produkte" className="hover:text-red-700 transition-colors">
                        {t("Produkte", "Products")}
                    </Link>
                    <span className="mx-2">/</span>
                    <span className="text-slate-600">{t("SAT-ZF-Verteilsysteme", "SAT-IF distribution systems")}</span>
                </p>

                <Reveal>
                    <div className="mt-6 max-w-3xl">
                        <Eyebrow>{t("JULTEC · KATHREIN · Verteiltechnik", "JULTEC · KATHREIN · Distribution technology")}</Eyebrow>
                        <h1 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
                            {t("Multischalter & Verteiltechnik von Jultec und Kathrein.", "Multi-switches & distribution technology by Jultec and Kathrein.")}
                        </h1>
                        <p className="mt-5 text-slate-600 leading-relaxed text-sm sm:text-base">
                            {t(
                                "SAT-ZF-Verteiltechnik für Mehrteilnehmeranlagen: Multischalter, Verteiler und Verstärker führender Hersteller — für Mehrfamilienhäuser, Hotels und Großobjekte. Alles auf dieser einen Seite:",
                                "SAT-IF distribution technology for multi-subscriber systems: multi-switches, splitters and amplifiers from leading manufacturers — for apartment blocks, hotels and large sites. Everything on this one page:"
                            )}
                        </p>
                    </div>
                </Reveal>

                <div className="mt-8 flex flex-wrap gap-3" data-testid="satzf-anchor-nav">
                    <a href="#jultec" data-testid="satzf-anchor-jultec" className={chip}>
                        {t("Jultec JRM-Serie", "Jultec JRM series")}
                    </a>
                    <a href="#jrs" data-testid="satzf-anchor-jrs" className={chip}>
                        JRS-System
                    </a>
                    <a href="#glasfaser" data-testid="satzf-anchor-glasfaser" className={chip}>
                        {t("Glasfaser (Optik)", "Fibre optic (optics)")}
                    </a>
                    <a href="#kathrein" data-testid="satzf-anchor-kathrein" className={chip}>
                        Kathrein
                    </a>
                </div>

                {/* ================= 1) JULTEC JRM-SERIE ================= */}
                <section id="jultec" className="mt-16 scroll-mt-28" data-testid="satzf-jultec-section">
                    <Reveal>
                        <Eyebrow>{t("JULTEC · Multischalter", "JULTEC · Multi-switches")}</Eyebrow>
                        <h2 className="mt-4 font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
                            {t("Multischalter: die JRM-Serie.", "Multi-switches: the JRM series.")}
                        </h2>
                        <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed">
                            {t("Die Typenbezeichnung verrät alles — Beispiel", "The type designation reveals everything — example")}{" "}
                            <span className="font-mono text-red-700">JRM0512M</span>
                            {t(": 1 Satellit (05), 12 Ausgänge (12), Kaskadenstart (M). Das Schluss-", ": 1 satellite (05), 12 outputs (12), cascade start (M). The final-")}{" "}
                            <span className="font-mono text-red-700">T</span>{" "}
                            {t("schließt die Kaskade ab.", "terminates the cascade.")}
                        </p>
                    </Reveal>
                    <Reveal>
                        <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white" data-testid="jultec-ms-table">
                            <table className="w-full text-sm min-w-[560px]">
                                <thead>
                                    <tr className="bg-slate-50 text-left font-mono text-[11px] uppercase tracking-widest text-slate-500">
                                        <th className="px-5 py-3 font-medium">{t("Ausgänge / Empfangsgeräte", "Outputs / receive units")}</th>
                                        {MS_COLS_DE.map((c, ci) => (
                                            <th key={c} className="px-5 py-3 font-medium">{t(c, MS_COLS_EN[ci])}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {MS_ROWS.map((r) => (
                                        <tr key={r.outsDe} className="hover:bg-slate-50/70 transition-colors">
                                            <td className="px-5 py-3.5 font-semibold text-slate-900 whitespace-nowrap">{t(r.outsDe, r.outsEn)}</td>
                                            {r.models.map((m, i) => (
                                                <td key={i} className="px-5 py-3.5 font-mono text-red-700 whitespace-nowrap">{m}</td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <ul className="mt-5 space-y-3" data-testid="jultec-ms-hints">
                            {MS_HINTS.map((h, i) => (
                                <li key={i} className="flex items-start gap-3 text-sm sm:text-base text-slate-700">
                                    <Check className="h-4 w-4 mt-1 shrink-0 text-emerald-600" />
                                    <span>{t(h.de, h.en)}</span>
                                </li>
                            ))}
                        </ul>
                        <a
                            href="/datenblaetter/jultec_katalog_13.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="jultec-katalog-link"
                            className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-red-700 hover:text-red-800 border-b border-red-300 pb-0.5 transition-colors"
                        >
                            {t("JULTEC-Katalog Edition 13 (PDF)", "JULTEC catalogue edition 13 (PDF)")}
                        </a>
                        <p className="mt-2 text-[11px] text-slate-400">
                            {t(
                                "Angaben nach jultec.de (Stand: Oktober 2026) — JULTEC ist eine Marke der JULTEC GmbH.",
                                "Information based on jultec.de (as of October 2026) — JULTEC is a trademark of JULTEC GmbH."
                            )}
                        </p>
                    </Reveal>
                </section>

                {/* ================= 2) JULTEC JRS-SYSTEM ================= */}
                <section id="jrs" className="mt-16 scroll-mt-28" data-testid="satzf-jrs-section">
                    <Reveal>
                        <Eyebrow>{t("JULTEC JRS · Komplett receivergespeist", "JULTEC JRS · Fully receiver-powered")}</Eyebrow>
                        <h2 className="mt-4 font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
                            {t("Das JRS-System: komplette SAT-Verteilung ohne Netzteil.", "The JRS system: complete satellite distribution without a power supply.")}
                        </h2>
                        <p className="mt-4 text-slate-600 leading-relaxed text-sm sm:text-base max-w-3xl">
                            {t(
                                "Die JRS-Serie von JULTEC ist eine Reihe teilnehmergesteuerter Einkabelumsetzer, die ihre Versorgung — inklusive LNB-Speisung — vollständig aus den angeschlossenen Receivern beziehen. Ein eigenes Netzteil ist nicht nötig; nur die tatsächlich benötigten Schaltungsteile werden aktiviert.",
                                "The JRS series from JULTEC is a range of subscriber-controlled single-cable transmodulators that draw their power — including the LNB feed — entirely from the connected receivers. No separate power supply is needed; only the circuit sections actually required are activated."
                            )}
                        </p>
                        <p className="mt-4 text-slate-600 leading-relaxed text-sm sm:text-base max-w-3xl">
                            {t(
                                "Damit lassen sich Ein- und Zweifamilienhäuser bis hin zu kaskadierten Mehrfamilienanlagen komplett receivergespeist versorgen — egal ob einzelne Receiver, Twin-/Quad-Tuner oder SAT>IP-Umsetzer. Maßgeblich ist die Anzahl der Empfangsteile (Userbänder), nicht die Anzahl der Antennendosen.",
                                "This makes it possible to supply single- and two-family homes right through to cascaded multi-family systems entirely from receiver power — whether single receivers, twin/quad tuners or SAT>IP transmodulators. What matters is the number of receive parts (user bands), not the number of wall outlets."
                            )}
                        </p>
                    </Reveal>

                    <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="jrs-features">
                        {JRS_FEATURES.map(({ icon: Icon, de, en: enF }, i) => (
                            <Reveal key={de.t} delay={(i % 4) * 0.06}>
                                <div className="h-full rounded-xl bg-white border border-slate-200 p-6 shadow-sm" data-testid={`jrs-feature-${i}`}>
                                    <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-red-50 border border-red-100 text-red-700">
                                        <Icon className="h-4.5 w-4.5" />
                                    </span>
                                    <h3 className="mt-4 font-display font-bold text-slate-900 text-sm sm:text-base">{t(de.t, enF.t)}</h3>
                                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t(de.d, enF.d)}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal>
                        <div className="mt-8 grid lg:grid-cols-5 gap-4" data-testid="jrs-visuals">
                            <figure className="lg:col-span-2 rounded-xl bg-white border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                                <div className="h-44 sm:h-52 lg:h-auto lg:flex-1 flex items-center justify-center bg-white p-4 overflow-hidden">
                                    <img
                                        src="/produkte/jultec-jps0904-4m.webp"
                                        alt="JULTEC JPS0904-4M Cascadable Channel Stacker"
                                        loading="lazy"
                                        className="max-h-full lg:max-h-56 max-w-full object-contain rounded"
                                    />
                                </div>
                                <figcaption className="px-4 py-3 text-xs text-slate-500 border-t border-slate-100 leading-relaxed">
                                    {t("JPS0904-4M — Cascadable Channel Stacker aus der Jultec-CSS-Familie.", "JPS0904-4M — cascadable channel stacker from the Jultec CSS family.")}
                                </figcaption>
                            </figure>
                            <figure className="lg:col-span-3 rounded-xl bg-white border border-slate-200 shadow-sm overflow-hidden flex flex-col" data-testid="jrs-schema">
                                <div className="flex-1 flex items-center justify-center bg-white px-3 py-4 sm:px-8 sm:py-6">
                                    <SchemaGraphic />
                                </div>
                                <figcaption className="px-4 py-3 text-xs text-slate-500 border-t border-slate-100 leading-relaxed" data-testid="jrs-schema-caption">
                                    {t(
                                        "Prinzipschema: Ein JRS-Gerät hat 4 Ausgänge (Ableitungen) — an jedem Ausgang hängen bis zu 4 Receiver. Quatro-LNB und Gerät werden komplett von den Receivern gespeist — kein Netzteil nötig.",
                                        "Schematic: a JRS unit has 4 outputs (drops) — each output feeds up to 4 receivers. Quatro LNB and the unit itself are powered entirely by the receivers — no power supply needed."
                                    )}
                                </figcaption>
                            </figure>
                        </div>
                    </Reveal>

                    <Reveal>
                        <h3 className="mt-12 font-display text-xl sm:text-2xl font-bold text-slate-900">
                            {t("Welche Geräte gibt es?", "Which devices are available?")}
                        </h3>
                        <p className="mt-2 text-sm text-slate-600 max-w-3xl">
                            {t(
                                "Die Gerätebezeichnung verrät die Ausbaustufe: JRS0504-8 bedeutet 4 Ableitungen mit je 8 Userbändern. Die Kombigeräte bedienen zusätzlich terrestrische Zuführung.",
                                "The model name reveals the expansion stage: JRS0504-8 means 4 drops with 8 user bands each. The combi units also handle a terrestrial feed."
                            )}
                        </p>
                        <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white" data-testid="jrs-table">
                            <table className="w-full text-sm min-w-[480px]">
                                <thead>
                                    <tr className="bg-slate-50 text-left font-mono text-[11px] uppercase tracking-widest text-slate-500">
                                        <th className="px-5 py-3 font-medium">{t("Ableitungen", "Drops")}</th>
                                        <th className="px-5 py-3 font-medium">{t("4 Userbänder je Ableitung", "4 user bands per drop")}</th>
                                        <th className="px-5 py-3 font-medium">{t("8 Userbänder je Ableitung", "8 user bands per drop")}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {JRS_ROWS.map((r) => (
                                        <tr key={r.ub4 + r.ub8} className="hover:bg-slate-50/70 transition-colors">
                                            <td className="px-5 py-3.5 font-semibold text-slate-900">{t(r.tapsDe, r.tapsEn)}</td>
                                            <td className="px-5 py-3.5 font-mono text-red-700">{r.ub4}</td>
                                            <td className="px-5 py-3.5 font-mono text-red-700">{r.ub8}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p className="mt-4 text-sm text-slate-600">
                            {t("Spezielle Kombigeräte mit zusätzlichem Terr-Ableiter:", "Special combi units with an additional Ter output:")}{" "}
                            <span className="font-mono text-red-700">{JRS_COMBI.join(" · ")}</span>
                        </p>
                    </Reveal>

                    <div className="mt-12 grid lg:grid-cols-2 gap-10 items-start">
                        <Reveal>
                            <h3 className="font-display text-xl sm:text-2xl font-bold text-slate-900">{t("Gut zu wissen in der Praxis", "Good to know in practice")}</h3>
                            <ul className="mt-5 space-y-3" data-testid="jrs-hints">
                                {JRS_HINTS.map((h) => (
                                    <li key={h.de.slice(0, 20)} className="flex items-start gap-3 text-sm sm:text-base text-slate-700">
                                        <Check className="h-4 w-4 mt-1 shrink-0 text-emerald-600" />
                                        <span>{t(h.de, h.en)}</span>
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h3 className="font-display text-xl sm:text-2xl font-bold text-slate-900">{t("Unterlagen & Links", "Documents & links")}</h3>
                            <div className="mt-5 space-y-3" data-testid="jrs-links">
                                {JRS_LINKS.map((l) => (
                                    <a
                                        key={l.href}
                                        href={l.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-between gap-4 rounded-xl bg-white border border-slate-200 px-5 py-4 shadow-sm hover:border-red-300 transition-colors"
                                    >
                                        <span className="inline-flex items-center gap-3 text-sm font-semibold text-slate-800">
                                            <FileDown className="h-4 w-4 text-red-700" />
                                            {t(l.de, l.en)}
                                        </span>
                                        <ArrowUpRight className="h-4 w-4 text-slate-400" />
                                    </a>
                                ))}
                            </div>
                            <p className="mt-4 text-xs text-slate-400">
                                {t(
                                    "Angaben nach jultec.de (Stand: Oktober 2026), ohne Gewähr — maßgeblich sind die Herstellerunterlagen. JULTEC und a²CSS sind Marken der JULTEC GmbH.",
                                    "Information based on jultec.de (as of October 2026), provided without warranty — the manufacturer's documentation is authoritative. JULTEC and a²CSS are trademarks of JULTEC GmbH."
                                )}
                            </p>
                        </Reveal>
                    </div>
                </section>

                {/* ================= 3) JULTEC GLASFASER ================= */}
                <section id="glasfaser" className="mt-16 scroll-mt-28" data-testid="satzf-glasfaser-section">
                    <Reveal>
                        <Eyebrow>{t("JULTEC · Glasfaser", "JULTEC · Fibre optic")}</Eyebrow>
                        <h2 className="mt-4 font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
                            {t("Glasfaserprodukte (Optik).", "Fibre optic products (optics).")}
                        </h2>
                        <p className="mt-4 text-slate-600 leading-relaxed text-sm sm:text-base max-w-3xl">
                            {t(
                                "JULTEC überträgt Satellitensignale auch glasfaserbasiert — nahezu dämpfungsfrei über große Entfernungen. Vier Produktbereiche decken die Glasfaserkette vom Sender bis zum Teilnehmer ab:",
                                "JULTEC also transmits satellite signals via fibre optic — virtually loss-free over long distances. Four product areas cover the fibre optic chain from transmitter to subscriber:"
                            )}
                        </p>
                    </Reveal>
                    <Reveal>
                        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3.5" data-testid="jultec-optics">
                            {OPTICAL.map((o, i) => (
                                <div
                                    key={o.name}
                                    className="h-full rounded-xl bg-white border border-slate-200 shadow-sm p-5 flex flex-col"
                                    data-testid={`jultec-optic-${i}`}
                                >
                                    {o.img && (
                                        <div className="h-24 flex items-center justify-center overflow-hidden mb-3">
                                            <img src={o.img} alt={`JULTEC ${o.name}`} loading="lazy" className="max-h-full max-w-full object-contain rounded" />
                                        </div>
                                    )}
                                    <p className="font-mono text-[11px] text-red-700">{t(o.name, o.nameEn)}</p>
                                    <p className="mt-1.5 text-xs text-slate-600 leading-relaxed">{t(o.desc, o.descEn)}</p>
                                </div>
                            ))}
                        </div>
                        <a
                            href="/datenblaetter/jultec_glasprodukte_2024.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="jultec-flyer-link"
                            className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-red-700 hover:text-red-800 border-b border-red-300 pb-0.5 transition-colors"
                        >
                            {t("Glasfaser-Flyer von JULTEC (PDF)", "JULTEC fibre optic flyer (PDF)")}
                        </a>
                        <p className="mt-2 text-[11px] text-slate-400">
                            {t(
                                "Angaben nach jultec.de (Stand: Oktober 2026) — JULTEC ist eine Marke der JULTEC GmbH.",
                                "Information based on jultec.de (as of October 2026) — JULTEC is a trademark of JULTEC GmbH."
                            )}
                        </p>
                    </Reveal>
                </section>

                {/* ================= 4) KATHREIN ================= */}
                <section id="kathrein" className="mt-16 scroll-mt-28" data-testid="satzf-kathrein-section">
                    <Reveal>
                        <Eyebrow>{t("KATHREIN · Verteiltechnik", "KATHREIN · Distribution technology")}</Eyebrow>
                        <h2 className="mt-4 font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
                            {t("KATHREIN-Multischalter im Überblick.", "KATHREIN multi-switches at a glance.")}
                        </h2>
                        <p className="mt-4 text-slate-600 leading-relaxed text-sm sm:text-base max-w-3xl">
                            {t(
                                "KATHREIN-Multischalter verteilen Satelliten- und terrestrische Signale zuverlässig an mehrere Teilnehmer, Wohneinheiten oder ganze Gebäudestrukturen — von klassischen Multischaltern über Einkabel-Systeme bis zu Kaskaden-Zubehör und Netzverstärkern.",
                                "KATHREIN multi-switches reliably distribute satellite and terrestrial signals to multiple subscribers, dwelling units or entire building structures — from classic multi-switches via single-cable systems to cascade accessories and network amplifiers."
                            )}
                        </p>
                    </Reveal>

                    <Reveal>
                        <p className="mt-8 text-sm text-slate-600 max-w-3xl">
                            <span className="font-display font-bold text-slate-900">Kathrein Line: </span>
                            {t(
                                "klassische Multischalter, kaskadierbare Lösungen und Einkabel-Multischalter — vom kompakten Einfamilienhaus-Gerät bis zur kaskadierbaren Anlage, auch für Modernisierungen mit bestehender Kabelstruktur.",
                                "classic multi-switches, cascadable solutions and single-cable multi-switches — from the compact single-family-home unit to the cascadable system, also for modernisations with existing cable infrastructure."
                            )}
                        </p>
                    </Reveal>

                    <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3.5" data-testid="kathrein-cards">
                        {KATHREIN_CARDS.map((c, i) => (
                            <Reveal key={c.name} delay={(i % 4) * 0.06}>
                                <div className="h-full rounded-xl bg-white border border-slate-200 shadow-sm p-5 flex flex-col" data-testid={`kathrein-card-${i}`}>
                                    <div className="h-24 flex items-center justify-center overflow-hidden mb-3">
                                        <img src={c.img} alt={`KATHREIN ${c.name}`} loading="lazy" className="max-h-full max-w-full object-contain rounded" />
                                    </div>
                                    <p className="font-mono text-[11px] text-red-700">{c.name}</p>
                                    <p className="mt-1.5 text-xs text-slate-600 leading-relaxed">{t(c.desc, c.descEn)}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    {LINE_GROUPS.map(({ groupDe, groupEn, rows }, gi) => (
                        <Reveal key={groupDe} delay={gi * 0.05}>
                            <h3 className="mt-8 font-mono text-[11px] uppercase tracking-widest text-red-700">{t(groupDe, groupEn)}</h3>
                            <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white" data-testid={`kathrein-table-${gi}`}>
                                <table className="w-full text-sm min-w-[560px]">
                                    <thead>
                                        <tr className="bg-slate-50 text-left font-mono text-[11px] uppercase tracking-widest text-slate-500">
                                            <th className="px-5 py-3 font-medium">{t("Modelle", "Models")}</th>
                                            <th className="px-5 py-3 font-medium">{t("Ausstattung", "Specification")}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {rows.map((r) => (
                                            <tr key={r.models} className="hover:bg-slate-50/70 transition-colors">
                                                <td className="px-5 py-3.5 font-mono text-red-700">{r.models}</td>
                                                <td className="px-5 py-3.5 text-slate-700">{t(r.specDe, r.specEn)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Reveal>
                    ))}
                </section>

                <Cta />

                <div className="mt-8">
                    <Link
                        to="/produkte"
                        data-testid="satzf-back"
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-red-700 transition-colors"
                    >
                        <ArrowRight className="h-4 w-4 rotate-180" />
                        {t("Zur Produktübersicht", "Back to products")}
                    </Link>
                </div>
            </div>
        </main>
    );
}
